import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useFeathers } from '../../app/util';
import {
  CTAButton,
  CloseButton,
  DistrictSelectInput,
  TypeDropdown
} from '../../components';
import { Chip, TextField } from '@mui/material';
import { humanizeDistrictType } from '../../app/util/humanize-district-type';
import { IssueSelectInput } from '../IssueSelectInput';
import { stateCodeForName } from '../../app/util/clean-states';

const CreateOffice = ({
  onClose,
  onCreate,
  seedState,
  seedName,
  suggestIssuesToCover = false
}) => {
  const feathers = useFeathers();
  const [ formInputs, setFormInputs ] = useState({
    name: seedName ?? '',
    state: seedState ? {
      ...seedState,
      text: seedState.longName ?? seedState.name
    } : null,
    districtTypes: [],
    issuesToCover: []
  })
  const submittable = formInputs?.name?.length > 0 && formInputs?.state && formInputs?.districtTypes?.length > 0;
  const [ stateConfiguration, setStateConfiguration ] = useState(null);
  const [ suggestedFromOffice, setSuggestedFromOffice ] = useState(null);
  const suggestionsRequest = useRef(null);

  useEffect(() => {
    loadStateConfiguration()
  }, [ formInputs?.state ])

  useEffect(() => {
    if(!suggestIssuesToCover || !seedName || !feathers) return;

    suggestionsRequest.current ||= feathers.getService('issues').find({
      query: {
        $issuesToCoverForOffice: seedName
      }
    });
    let cancelled = false;

    (async () => {
      try {
        const result = await suggestionsRequest.current;
        if(cancelled || !result?.data?.length) return;

        setFormInputs(currentInputs => {
          const existingKeys = new Set(
            (currentInputs.issuesToCover || [])
              .map(issue => issue?.key || issue?.issue?.key)
              .filter(Boolean)
          );
          const suggestedIssues = result.data
            .filter(issue => {
              if(!issue?.key || existingKeys.has(issue.key)) return false;
              existingKeys.add(issue.key);
              return true;
            })
            .map(issue => ({ key: issue.key, name: issue.name }));

          return {
            ...currentInputs,
            issuesToCover: (currentInputs.issuesToCover || []).concat(suggestedIssues)
          };
        });

        if(result.suggestedFromOffice?.name && result.suggestedFromOffice?.state) {
          setSuggestedFromOffice(result.suggestedFromOffice);
        }
      } catch(_error) {
        // Suggestions are optional; leave the field editable if the request fails.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ feathers, seedName, suggestIssuesToCover ]);

  useEffect(() => {
    if (!seedState || formInputs?.state) return;

    setFormInputs(currentInputs => ({
      ...currentInputs,
      state: {
        ...seedState,
        text: seedState.longName ?? seedState.name
      }
    }))
  }, [ seedState, formInputs?.state ])

  const loadStateConfiguration = async () => {
    const stateCode = stateCodeForName(formInputs?.state?.name);

    if (!formInputs?.state) return;

    const config = await feathers.getService('state-configurations').get(stateCode.toLowerCase());
    setStateConfiguration(config);
  }

  const onChange = e => {
    const { name, value } = e.target;
    const update = {};
    update[name] = value;
    let newInputs = {
      ...formInputs,
      ...update
    };

    // Maintain the workflow for the inputs
    setFormInputs(newInputs)
  }

  const [ submitting, setSubmitting ] = useState(false);
  const [ error, setError ] = useState(false);
  const [ issueInputValue, setIssueInputValue ] = useState(null);
  const submit = async () => {
    if(!submittable || submitting) return;

    setSubmitting(true);
    setError(false);
    const mappedInputs = {
      name: formInputs?.name,
      district: formInputs?.state?._id,
      districtTypes: formInputs?.districtTypes,
      issuesToCover: formInputs?.issuesToCover?.map(issue => ({ key: issue?.key || issue?.issue?.key })).filter(issue => issue.key)
    }

    try {
      const office = await feathers.getService('offices').create(mappedInputs);
      if(onCreate) onCreate(office);
      onClose();

    } catch(err) {
      // Is error code a duplicate error?
      console.log(err)
      const errorAsJson = err.toJSON()
      setError(errorAsJson)
      setSubmitting(false);
    }
  }

  const districtTypesSorted = useMemo(() => {
    // sort by tier number and then by alphabetical
    return (stateConfiguration?.typeConfigurations || [])
      .sort((a, b) => {
        return a.tierNumber - b.tierNumber || a.type.localeCompare(b.type)
      })
      .filter(type => type.tierNumber >= 1)
      .filter(type => !formInputs?.districtTypes?.includes(type.type))

  }, [ stateConfiguration, formInputs?.districtTypes ])

  return (
    <Wrapper>
      <CloseButton
        onClick={onClose}
        style={{
          position: 'absolute',
          height: '36px',
          right: '-20px',
          top: '-20px'
        }}
      />
      <Top>
        {
          error && (
            <div style={{ color: 'red', marginBottom: '20px' }}>
              {error.message}
            </div>
          )
        }
        <h1>New Office</h1>
        <Row>
          <div className='label'>State</div>
          <DistrictSelectInput
            value={formInputs?.state}
            onChange={e => {
              console.log(e)
              setFormInputs({ ...formInputs, state: e.target.value, districtTypes: [] })
            }}
            style={{ flex: 1 }}
            districtTypes={['state']}
            multi={false}
          />
        </Row>
        <Row>
          <div className='label'>Name</div>
          <TextField
            name='name'
            value={formInputs?.name}
            onChange={onChange}
            variant='outlined'
            size='small'
            style={{ flex: 1 }}
          />
        </Row>
        <Row>
          <div className='label'>District types</div>
          <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap' }}>
            {
              formInputs?.districtTypes?.map((type, i) => (
                <Chip
                  key={i}
                  label={humanizeDistrictType(type, stateConfiguration?.typeConfigurations)}
                  onDelete={() => {
                    const newTypes = formInputs?.districtTypes.filter(t => t !== type)
                    setFormInputs({ ...formInputs, districtTypes: newTypes })
                  }}
                  style={{ margin: '4px' }}
                />
              ))
            }
            {
              stateConfiguration && (
                <TypeDropdown
                  placeholder={'Add type'}
                  value={null}
                  options={districtTypesSorted?.map(({ type }) => ({ type, title: humanizeDistrictType(type, stateConfiguration?.typeConfigurations || []) }))}
                  onChange={(newType) => {
                    const newTypes = formInputs?.districtTypes.concat(newType)
                    setFormInputs({ ...formInputs, districtTypes: newTypes })
                  }}
                />
              )
            }
          </div>
        </Row>
        <Row>
          <div className='label'>Issues to cover</div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
              {
                formInputs?.issuesToCover?.map((issue, i) => {
                  const issueKey = issue?.key || issue?.issue?.key;
                  const issueName = issue?.name || issue?.issue?.name || issue?.text;
                  if (!issueKey) return null;
                  return (
                    <Chip
                      key={i}
                      label={issueName}
                      onDelete={() => {
                        const newIssues = formInputs?.issuesToCover.filter((_, idx) => idx !== i)
                        setFormInputs({ ...formInputs, issuesToCover: newIssues })
                      }}
                      size='small'
                      style={{ margin: '2px' }}
                    />
                  );
                })
              }
              <div style={{ minWidth: '200px', flex: '0 0 auto' }}>
                <IssueSelectInput
                  candidateIssue={true}
                  value={issueInputValue}
                  excludeKeys={(formInputs?.issuesToCover || []).map(
                    issue => issue?.key || issue?.issue?.key
                  ).filter(Boolean)}
                  clearAfterSelect={true}
                  onChange={(e) => {
                    const selectedIssue = e.target.value;
                    if (selectedIssue && selectedIssue.key) {
                      const issueKey = selectedIssue.key;
                      // Check if issue already exists
                      const exists = formInputs?.issuesToCover?.some(
                        issue => (issue?.key || issue?.issue?.key) === issueKey
                      );
                      if (!exists) {
                        const newIssues = formInputs?.issuesToCover?.concat([{
                          key: selectedIssue.key,
                          name: selectedIssue.name || selectedIssue.text
                        }])
                        setFormInputs({ ...formInputs, issuesToCover: newIssues })
                      }
                      // Clear the input after selection
                      setIssueInputValue(null);
                    }
                  }}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            {
              suggestedFromOffice && (
                <div style={{ marginTop: '6px', fontSize: '12px', opacity: 0.65 }}>
                  Suggested based on {suggestedFromOffice.state}'s {suggestedFromOffice.name} issues
                </div>
              )
            }
          </div>
        </Row>
      </Top>
      <CTAButton
        value={submitting ? `Creating...` : `Create`}
        disabled={!submittable || submitting}
        onClick={submit}
      />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 500px;
  min-height: 200px;
`
const Top = styled.div`
  display: flex;
  flex-direction: column;

  h1 {
    font-size: 28px;
    margin: 0 0 26px;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  margin-bottom: 28px;

  div.label {
    font-size: 18px;
    margin: 0 14px 0 0;
    opacity: 0.7;
    width: 130px;
  }
`;

export { CreateOffice };
